import React from 'react';
import './Program.css';
import Reveal from '../shared/Reveal';
import SampleTag from '../shared/SampleTag';
import BallisticNumber from '../shared/BallisticNumber';

const PROGRAM_TYPES = [
    'Aerobic Training',
    'Lactate Threshold Training',
    'VO2max Training',
    'Anaerobic Training',
    'Race Warm Up',
    'Default Training'
];

// Illustrative session. Fields mirror the set form in ProgramCreator.jsx:
// set type, style, zone, repetitions, distance and rest.
const ITEMS = [
    { set: 'Warm Up', style: 'Free Style', zone: 'Rec', reps: 1, distance: 400, rest: null },
    { set: 'Drill Set', style: 'Free Style', zone: 'Rec', reps: 4, distance: 50, rest: 15 },
    {
        set: 'Main Set',
        style: 'Free Style',
        zone: 'End 1',
        reps: 6,
        distance: 200,
        rest: 20,
        sub: { set: 'Sub-item', style: 'Free Style', zone: 'End 2', reps: 2, distance: 100, rest: 20 }
    },
    { set: 'Cool Down', style: 'Free Style', zone: 'Rec', reps: 1, distance: 200, rest: null }
];

const volume = item => item.reps * item.distance + (item.sub ? item.sub.reps * item.sub.distance : 0);
const TOTAL = ITEMS.reduce((sum, item) => sum + volume(item), 0);
// ProgramCreator shows ideal warm up as 15% of the total and cool down as 5%.
const IDEAL_WARM_UP = Math.round(TOTAL * 0.15);
const IDEAL_COOL_DOWN = Math.round(TOTAL * 0.05);

const formatMetres = value => `${Math.round(value).toLocaleString('en-US')}m`;

function describe(item) {
    const rest = item.rest ? `, rest ${item.rest} sec` : '';
    return `${item.reps} x ${item.distance}m ${item.style}, ${item.zone}${rest}`;
}

export default function Program() {
    return (
        <section id='programs' className='lp-section lp-program'>
            <div className='lp-wrap'>
                <Reveal>
                    <p className='lp-eyebrow'>Program Creator</p>
                    <h2 className='lp-h2'>What a coach builds with those zones.</h2>
                    <p className='lp-lede'>
                        A program is a typed list of sets. Each set picks a stroke, a zone, the reps
                        and the rest. The coach then puts the program on a group&apos;s calendar.
                    </p>
                </Reveal>

                <div className='lp-steps-head'>
                    <SampleTag>Example program</SampleTag>
                </div>

                <ol className='lp-steps'>
                    <Reveal as='li' className='lp-step lp-step-1'>
                        <span className='lp-step-level'>1 Program</span>
                        <h3>Aerobic Training</h3>
                        <p>
                            The coach names the program and picks a type. The product has six.
                        </p>
                        <ul className='lp-pills' aria-label='Program types'>
                            {PROGRAM_TYPES.map(type => (
                                <li key={type} className={type === 'Aerobic Training' ? 'is-on' : ''}>
                                    {type}
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal as='li' className='lp-step lp-step-2' delay={100}>
                        <span className='lp-step-level'>2 Program items</span>
                        <h3>Sets, each tied to a zone</h3>
                        <ul className='lp-sets'>
                            {ITEMS.map(item => (
                                <li key={item.set}>
                                    <div className='lp-set-row'>
                                        <span className='lp-set-name'>{item.set}</span>
                                        <span className='lp-set-desc lp-num'>{describe(item)}</span>
                                        <span className='lp-set-vol lp-num'>{formatMetres(item.reps * item.distance)}</span>
                                    </div>
                                    {item.sub && (
                                        <div className='lp-set-row lp-set-sub'>
                                            <span className='lp-set-name'>{item.sub.set}</span>
                                            <span className='lp-set-desc lp-num'>{describe(item.sub)}</span>
                                            <span className='lp-set-vol lp-num'>
                                                {formatMetres(item.sub.reps * item.sub.distance)}
                                            </span>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal as='li' className='lp-step lp-step-3' delay={200}>
                        <span className='lp-step-level'>3 Training information</span>
                        <h3>The session adds itself up</h3>
                        <dl className='lp-totals'>
                            <div>
                                <dt>Total distance</dt>
                                <dd>
                                    <BallisticNumber value={TOTAL} from={0} format={formatMetres} text={formatMetres(TOTAL)} />
                                </dd>
                            </div>
                            <div>
                                <dt>Ideal warm up</dt>
                                <dd>
                                    <BallisticNumber
                                        value={IDEAL_WARM_UP}
                                        from={0}
                                        format={formatMetres}
                                        text={formatMetres(IDEAL_WARM_UP)}
                                    />
                                </dd>
                            </div>
                            <div>
                                <dt>Ideal cool down</dt>
                                <dd>
                                    <BallisticNumber
                                        value={IDEAL_COOL_DOWN}
                                        from={0}
                                        format={formatMetres}
                                        text={formatMetres(IDEAL_COOL_DOWN)}
                                    />
                                </dd>
                            </div>
                        </dl>
                        <p>
                            The editor also charts the split by stroke, set type and zone.
                        </p>
                    </Reveal>

                    <Reveal as='li' className='lp-step lp-step-4' delay={300}>
                        <span className='lp-step-level'>4 Workout</span>
                        <h3>Scheduled for the group</h3>
                        <p>
                            On a group&apos;s page the coach picks the program, a date and a time. It
                            lands on the calendar and in attendance.
                        </p>
                    </Reveal>
                </ol>
            </div>
        </section>
    );
}
